import "./KaboodleToaster.scss";

import type { ToastPosition } from "react-hot-toast";
import { toast, Toaster } from "react-hot-toast";
import { extractErrorMessage } from "utils/error-utils";

type KaboodleToasterPropTypes = {
  position?: ToastPosition;
  extraOptions?: Record<string, string | number>;
  durationInMilliseconds?: number;
};

export const DURATION_IN_MS_BEFORE_DISAPPEARING = 3000;
export const TOASTER_SUCCESS_MSG = "Data saved successfully";

// static methods for consistent toast usage throughout the app
const KaboodleToaster = {
  success: (
    message: string = TOASTER_SUCCESS_MSG,
    options?: { duration?: number },
  ) => {
    toast.success(message, {
      className: "KaboodleToaster KaboodleToaster--Success",
      duration: options?.duration ?? DURATION_IN_MS_BEFORE_DISAPPEARING,
      icon: (
        <span className="material-icons" style={{ color: "green" }}>
          check_circle
        </span>
      ),
    });
  },

  error: (error: unknown, options?: { duration?: number }) => {
    // automatically extract error message if it's not already a string
    const message =
      typeof error === "string"
        ? error
        : extractErrorMessage(error, "An error occurred");

    toast.error(message, {
      className: "KaboodleToaster KaboodleToaster--Error",
      duration: options?.duration ?? DURATION_IN_MS_BEFORE_DISAPPEARING,
    });
  },

  info: (message: string, options?: { duration?: number }) => {
    toast(message, {
      className: "KaboodleToaster KaboodleToaster--Info",
      duration: options?.duration ?? DURATION_IN_MS_BEFORE_DISAPPEARING,
      icon: (
        <span className="material-icons" style={{ color: "#3c1283" }}>
          info
        </span>
      ),
    });
  },

  custom: (message: string, options?: { duration?: number }) => {
    toast(message, {
      className: "KaboodleToaster KaboodleToaster--Custom",
      duration: options?.duration ?? DURATION_IN_MS_BEFORE_DISAPPEARING,
    });
  },

  loading: (message: string, options?: { duration?: number }) => {
    return toast.loading(message, {
      className: "KaboodleToaster KaboodleToaster--Loading",
      duration: options?.duration ?? DURATION_IN_MS_BEFORE_DISAPPEARING,
    });
  },

  // promise-aware loading toast that automatically handles success/error states
  promiseLoading: <T,>(
    promise: Promise<T>,
    {
      loadingMessage = "Loading...",
      successMessage = TOASTER_SUCCESS_MSG,
      errorMessage,
    }: {
      loadingMessage?: string;
      successMessage?: string;
      errorMessage?: string;
    } = {},
  ): Promise<T> => {
    return toast.promise(
      promise,
      {
        loading: loadingMessage,
        success: successMessage,
        error: (err) =>
          errorMessage ?? extractErrorMessage(err, "An error occurred"),
      },
      {
        className: "KaboodleToaster",
        loading: {
          className: "KaboodleToaster KaboodleToaster--Loading",
          duration: Infinity,
        },
        success: {
          className: "KaboodleToaster KaboodleToaster--Success",
          icon: (
            <span className="material-icons" style={{ color: "green" }}>
              check_circle
            </span>
          ),
          duration: DURATION_IN_MS_BEFORE_DISAPPEARING,
        },
        error: {
          className: "KaboodleToaster KaboodleToaster--Error",
        },
      },
    );
  },

  // component for global toast configuration
  ToastContainer: ({
    position = "bottom-right",
    extraOptions = {},
    durationInMilliseconds = DURATION_IN_MS_BEFORE_DISAPPEARING,
  }: KaboodleToasterPropTypes) => {
    const commonProps = {
      duration: durationInMilliseconds,
    };

    return (
      <Toaster
        position={position}
        toastOptions={{
          success: {
            className: "KaboodleToaster KaboodleToaster--Success",
            icon: (
              <span className="material-icons" style={{ color: "green" }}>
                check_circle
              </span>
            ),
            ...commonProps,
          },
          error: {
            className: "KaboodleToaster KaboodleToaster--Error",
            ...commonProps,
          },
          custom: {
            className: "KaboodleToaster KaboodleToaster--Custom",
            ...commonProps,
          },
          loading: {
            className: "KaboodleToaster KaboodleToaster--Loading",
            ...commonProps,
          },
          ...extraOptions,
        }}
      />
    );
  },
};

export default KaboodleToaster;
